/**
 * CRYPTO_KEY 관리
 * @module shared/utils/crypto/CommonUtil.ts
 * @author Lian
 */
import axios from 'axios';

import RSAKey from '@/shared/utils/crypto/rsa.js';
/**
 * 공개키 토큰 response 인터페이스
 * @author Lian
 */
interface RsaTokenInterface {
  /**
   * 공개키 Modulus
   */
  publicKeyModulus: string;
  /**
   * 공개키 Exponent
   */
  publicKeyExponent: string;
}

export const CryptoManager = {
  key: null as RSAKey | null,
  initPromise: null as Promise<RSAKey> | null,

  setKey(publicKeyModulus: string, publicKeyExponent: string) {
    if (!publicKeyModulus || !publicKeyExponent) {
      throw new Error('공개키 정보가 누락되었습니다.');
    }

    const rsa = new RSAKey();
    rsa.setPublic(publicKeyModulus, publicKeyExponent);

    if (!rsa.n || !rsa.e) {
      throw new Error('RSA 공개키 설정 실패');
    }

    this.key = rsa;
    return rsa;
  },

  async fetchKey(): Promise<RSAKey> {
    const { data } = await axios.get<RsaTokenInterface>('/api/public/tokens', {
      withCredentials: true,
    });

    const { publicKeyModulus, publicKeyExponent } = data;

    return this.setKey(publicKeyModulus, publicKeyExponent);
  },

  async getKey(): Promise<RSAKey> {
    if (this.key) return this.key;

    if (!this.initPromise) {
      this.initPromise = this.fetchKey()
        .then(rsa => {
          this.key = rsa;
          return rsa;
        })
        .finally(() => {
          this.initPromise = null;
        });
    }

    return this.initPromise;
  },

  reset() {
    this.key = null;
    this.initPromise = null;
  },
};
